import { Component, DoCheck, ElementRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TsInputComponent } from 'tsui';

import unformat from 'accounting-js/lib/unformat.js';
import UrlUtils from '@shared/Utils/url.utils';
import { FormatNumberPipe } from '@shared/pipes/format-number.pipe';
import { FormFieldComponent } from '../form-field.types';

@Component({
  templateUrl: './number-form-field.component.html',
  styleUrls: ['./../form-field.component.scss'],
})
export class NumberFormFieldComponent extends FormFieldComponent implements OnInit, DoCheck {
  @ViewChild(TsInputComponent) private readonly tsInput?: TsInputComponent;

  private _originalValue: any;

  public value: any;
  public editing: boolean = false;
  public precision: number = 0;
  public readonly control = new FormControl<number | string | null>(null);
  // Overrides the inherited Material-specific selector: ts-input has no .mat-form-field-flex to target.
  public readonly commonTargetStylingClass = '.ts-input';

  // Duck-typed contract read by form-field.component.ts to avoid clobbering an in-progress edit.
  get inputControl(): ElementRef<HTMLInputElement> | undefined {
    return this.tsInput?.inputElement();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.value = this.model[this.fieldDef.name];

    this._originalValue = this.value;

    if (this.fieldDef.meta && this.fieldDef.meta.scale) {
      this.precision = this.fieldDef.meta.scale;
    }

    this.syncDisplay();
    this.updateDisabledState();

    this.control.valueChanges.pipe(takeUntilDestroyed(this.dRef)).subscribe((value) => {
      this.value = value;
    });
  }

  ngDoCheck(): void {
    this.updateDisabledState();
  }

  edit(): void {
    if (!this.editing && !this.isReadonly) {
      this.editing = true;
      this.syncDisplay();
      setTimeout(() => {
        this.inputControl?.nativeElement.focus();
      }, 300);
    }
  }

  onBlur(): void {
    this.value = UrlUtils.checkAndEvalFormula(this.value)?.toString();
    if (this.value === '') {
      this.value = null;
    }
    this.onSave();
    this.editing = false;
    this.syncDisplay();
  }

  updateChangedModel(model: any): void {
    this.model = model;
    this.value = this.model[this.fieldDef.name];
    this._originalValue = this.value;

    this.syncDisplay();
  }

  private onSave(): void {
    if (this._originalValue != this.value) {
      this.updateModel();
      this.update.emit({ [this.fieldDef.name]: this.value ? this.value.replace(/,/g, '') : null });
    }
  }

  private updateModel(): void {
    this.model[this.fieldDef.name] = unformat(this.value);
    this._originalValue = unformat(this.value);
  }

  // Shows the raw value while editing and the formatted value otherwise. Writes without emitting so the
  // formatted text never replaces `value`.
  private syncDisplay(): void {
    const displayValue = this.editing
      ? this.value
      : new FormatNumberPipe().transform(this.value, this.precision, this.fieldDef);

    this.control.setValue(displayValue, { emitEvent: false });
  }

  private updateDisabledState(): void {
    if (this.isDisabled === this.control.disabled) {
      return;
    }

    if (this.isDisabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }
}
